import { AdvancedMarker, GoogleMapsContext, useMapsLibrary } from "@vis.gl/react-google-maps";
import React, { useContext, useEffect, useMemo } from "react";
import endingPointIcon from "../assets/endingPoint.svg";
import staringPointIcon from "../assets/startingPoint.svg";
import { MAX_LAT_DISTANCE, MAX_TOUR_DISTANCE, MIN_TOUR_DISTANCE } from "../constants";
import { OnDemandContext } from "../context";
import { useReactKey } from "../lib";

export function PathLine({ onPointDragStart, onPointDragEnd }) {
  const mapsApi = useMapsLibrary("maps");
  const coreApi = useMapsLibrary("core");
  const geometryApi = useMapsLibrary("geometry");

  const map = useContext(GoogleMapsContext)?.map;
  /**
   * Эти ключи используются, чтобы зафорсить рендер маркеров с новым значением position,
   * т.к. переданное в position значение влияет только на изначальное положение маркера.
   */
  const [starPointKey, refreshStartPointKey] = useReactKey();
  const [endPointKey, refreshEndpointKey] = useReactKey();

  const { startingPoint, endingPoint, changeStartingPoint, changeEndingPoint } =
    useContext(OnDemandContext);

  /* Создаём экземпляр линии, соединяющей точки */
  const dashedLine = useMemo(() => {
    if (!mapsApi || !coreApi) return null;

    return new mapsApi.Polyline({
      strokeOpacity: 1,
      strokeWeight: 8, // Line thickness
      strokeColor: "#FFF",
      visible: false,
      icons: [
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeOpacity: 1,
            strokeColor: "black",
            scale: 4,
          },
          offset: "0", // Offset the pattern
          repeat: "20px", // Repeat the pattern every 20px
        },
      ],
      zIndex: 20,
    });
  }, [mapsApi, coreApi]);

  /* Создаём экземпляр обводки для линии */
  const blueLine = useMemo(() => {
    if (!mapsApi || !coreApi) return null;

    return new mapsApi.Polyline({
      strokeColor: "rgba(0, 122, 255, .2)",
      visible: false,
      strokeWeight: 42,
      zIndex: 10,
    });
  }, [mapsApi, coreApi]);

  /* Добавляем линию и обводку на карту, и убираем при размонтировании */
  useEffect(() => {
    if (!map || !dashedLine || !blueLine) return;
    dashedLine.setMap(map);
    blueLine.setMap(map);
    return () => {
      dashedLine.setMap(null);
      blueLine.setMap(null);
    };
  }, [map, dashedLine, blueLine]);

  /* Обновляем видимость и координаты линии и её обводки */
  useEffect(() => {
    if (!dashedLine || !blueLine) return;
    if (!startingPoint || !endingPoint) {
      dashedLine.setVisible(false);
      blueLine.setVisible(false);
      return;
    }

    dashedLine.setPath([startingPoint, endingPoint]);
    dashedLine.setVisible(true);
    blueLine.setPath([startingPoint, endingPoint]);
    blueLine.setVisible(true);
  }, [dashedLine, blueLine, startingPoint, endingPoint]);

  /* Создаём экземпляр окружности для обозначения границ конечной точки. */
  const outerSafeZone = useMemo(() => {
    if (!mapsApi) return null;

    return new mapsApi.Circle({
      fillColor: "rgba(228, 70, 86, 0.2)",
      strokeColor: "rgba(228, 70, 86, 1)",
      fillOpacity: 1,
      strokeWeight: 1,
    });
  }, [mapsApi]);

  const innerSafeZone = useMemo(() => {
    if (!mapsApi) return null;

    return new mapsApi.Circle({
      fillColor: "rgba(228, 70, 86, 0.2)",
      strokeColor: "rgba(228, 70, 86, 1)",
      fillOpacity: 1,
      strokeWeight: 1,
    });
  }, [mapsApi]);

  /* Добавляем окружности на карту, и убираем при размонтировании */
  useEffect(() => {
    if (!map || !outerSafeZone || !innerSafeZone) return;
    outerSafeZone.setMap(map);
    innerSafeZone.setMap(map);

    return () => {
      outerSafeZone.setMap(null);
      innerSafeZone.setMap(null);
    };
  }, [outerSafeZone, innerSafeZone, map]);

  /* Обновляем видимость,координаты и радиус окружностей */
  useEffect(() => {
    if (!outerSafeZone || !innerSafeZone || !startingPoint) return;
    /**
     * Устанавливаем центр окружности по зеркально координатам стартовой точки
     * и радиус равный максимально допустимой широте за вычетом максимального радиуса туров.
     *
     * Это создаст эффект "вывернутой окружности" -
     * вся зона за её пределами будет перекрывать остальную карту.
     */
    outerSafeZone.setOptions({
      center: {
        lat: -startingPoint.lat,
        lng: startingPoint.lng - 180,
      },
      radius: MAX_LAT_DISTANCE - MAX_TOUR_DISTANCE,
      visible: true,
    });

    innerSafeZone.setOptions({
      center: startingPoint,
      radius: MIN_TOUR_DISTANCE,
      visible: true,
    });

    return () => {
      outerSafeZone.setVisible(false);
      innerSafeZone.setVisible(false);
    };
  }, [outerSafeZone, innerSafeZone, startingPoint]);

  useEffect(() => {
    if (outerSafeZone && innerSafeZone) {
      outerSafeZone.setOptions({
        clickable: !endingPoint,
      });
      innerSafeZone.setOptions({
        clickable: !endingPoint,
      });
    }
  }, [outerSafeZone, innerSafeZone, endingPoint]);

  const handleStartingPointtDrag = e => {
    if (!geometryApi) return;

    if (!endingPoint) {
      changeStartingPoint(e.latLng.toJSON());
      return;
    }

    const distance = geometryApi.spherical.computeDistanceBetween(e.latLng, endingPoint);

    if (distance <= MAX_TOUR_DISTANCE) {
      changeStartingPoint(e.latLng.toJSON());
    } else {
      changeStartingPoint(startingPoint);
      refreshStartPointKey();
    }
  };

  const handleEndpointDrag = e => {
    if (!geometryApi) return;

    const distance = geometryApi.spherical.computeDistanceBetween(e.latLng, startingPoint);

    if (distance <= MAX_TOUR_DISTANCE && distance >= MIN_TOUR_DISTANCE) {
      changeEndingPoint(e.latLng.toJSON());
    } else {
      changeEndingPoint(endingPoint);
      refreshEndpointKey();
    }
  };

  return (
    <React.Fragment>
      <AdvancedMarker
        key={starPointKey}
        draggable
        position={startingPoint}
        anchorPoint={["50%", "14px"]}
        onDragStart={onPointDragStart}
        onDragEnd={onPointDragEnd}
        onDrag={handleStartingPointtDrag}
      >
        <img width={42} src={staringPointIcon} />
      </AdvancedMarker>

      <AdvancedMarker
        key={endPointKey}
        draggable
        anchorPoint={["50%", "30px"]}
        position={endingPoint}
        onDrag={handleEndpointDrag}
      >
        <img width={44} src={endingPointIcon} />
      </AdvancedMarker>
    </React.Fragment>
  );
}
